/**
 * "You have picked twelve rows. Call them."
 *
 * The bar above a list, drawn only once something is picked, so a table nobody
 * is selecting from looks exactly as it did before. It is the only place the
 * run is started from - the row menus still place single calls, and that
 * separation is deliberate: one click on a number should never begin a list.
 *
 * The refusal is spelled out rather than swallowed. A selection can hold twelve
 * rows and no dialable number in any of them (scraped register text, numbers
 * already on the do-not-call list), and a button that appears to do nothing is
 * the worst answer available.
 */
import { useState } from "react";
import { PhoneOutgoing, X } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { CallTarget } from "../callState";
import { useCallContext } from "../CallProvider";
import type { QueuePicks } from "./useQueuePicks";

export function QueueBar({ picks }: { picks: QueuePicks | null }) {
  const t = useT();
  const ctx = useCallContext();
  /**
   * The exact selection a start found nothing in.
   *
   * The array rather than a boolean, because the message has to go away the
   * moment the operator changes their mind about a row and not a moment later.
   * `targets` keeps its identity until the selection changes, so comparing it
   * is precise where a flag would need an effect to clear it.
   */
  const [refused, setRefused] = useState<readonly CallTarget[] | null>(null);

  if (!picks || picks.size === 0) return null;

  const empty = refused === picks.targets;
  const start = () => {
    const started = ctx?.startQueue(picks.targets) ?? null;
    setRefused(started ? null : picks.targets);
  };

  return (
    <div className="crm-card mb-3.5 flex flex-wrap items-center gap-2.5 p-3.5 pl-5">
      <span className="crm-tnum text-[13px] font-bold">
        {t("calls.queue.selected", { n: picks.size })}
      </span>
      {/* Only ever after a start that found nothing. It stays until the
          selection changes, which is the only thing that can make it wrong. */}
      {empty ? <span className="crm-sub text-[12.5px]">{t("calls.queue.empty")}</span> : null}
      <span className="crm-push flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          className="crm-btn crm-btn-sm"
          onClick={() => {
            picks.clear();
            setRefused(null);
          }}
        >
          <X className="size-3.5" />
          {t("calls.queue.clear")}
        </button>
        <button type="button" className="crm-btn crm-btn-sm crm-btn-brand" onClick={start}>
          <PhoneOutgoing className="size-3.5" />
          {t("calls.queue.start")}
        </button>
      </span>
    </div>
  );
}
